import { Box, VStack, Text, Progress, useToast, IconButton, Tooltip, Collapse, Button, HStack, ScaleFade, Switch, FormControl, FormLabel, Flex } from '@chakra-ui/react'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { FaMicrophone, FaMicrophoneSlash, FaSignOutAlt, FaQrcode, FaTimes, FaBars, FaExpand, FaVolumeMute, FaVolumeUp, FaChartBar } from 'react-icons/fa'
import { subscribeToMatch, updateScore, addScoreEvent, undoLastScore, getMatch, updateTiebreakPointWithCooldown } from '../services/firebase'
import { database } from '../services/firebase'
import { QRCodeModal } from './modals/QRCodeModal'
import { MicrophoneSettingsModal } from './modals/MicrophoneSettingsModal'
import { Statistics } from './Statistics'
import { ScoreBox } from './scoring/ScoreBox'
import { TopBar } from './scoring/TopBar'
import { ExitModal } from './scoring/ExitModal'
import { useSpeechRecognition as useSpeechRecognitionController } from '@/hooks/useSpeechRecognitionRouter'
import { useAutoFullscreenChrome } from '@/hooks/useAutoFullscreenChrome'
import { useTennisMatchData } from '../hooks/useTennisMatchData'
import { useTennisMatchLogic } from '../hooks/useTennisMatchLogic'
import { useTiebreakLogic } from '../hooks/useTiebreakLogic'
import { GameResetOverlay } from './scoring/GameResetOverlay'
import { TennisScoreBox } from './scoring/TennisScoreBox'
import { getHexFromColor } from '../utils/colors'
import { getDisplayScore, getDisplayScores } from '../utils/tennisScoring'
import QRCode from 'react-qr-code'
import { ref, update, get, onValue, set } from 'firebase/database'
import { useScreenWakeLock } from '@/hooks/useScreenWakeLock'
import { usePaywallContext } from '@/contexts/PaywallContext'
import ClubLogo from '@/components/ClubLogo'
import { IPhoneModal } from '@/components/iPhoneModal'
import { InternetConnectionModal } from '@/components/InternetConnectionModal'

type TeamColor = 'red' | 'blue' | 'purple' | 'yellow' | 'orange' | 'green'

interface TennisScoringScreenProps {
  matchId: string
  team1Color: TeamColor
  team2Color: TeamColor
  isController?: boolean
  team1Name?: string
  team2Name?: string
  showQRCode?: boolean
  courtNumber?: number
}

// Patch: Extend MatchData type to include pendingReset for linter
declare module '../services/firebase' {
  interface MatchData {
    pendingReset?: { timer: number } | null;
  }
}

export function TennisScoringScreen({ matchId, team1Color, team2Color, isController: propIsController = false, team1Name, team2Name, showQRCode = true, courtNumber }: TennisScoringScreenProps) {
  // Determine if user is a controller based on URL token parameter (deployed version logic)
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const isController = propIsController || !!searchParams.get('token')
  const { userSubscription } = usePaywallContext()
  const isIndividualPlan = userSubscription?.planType === 'individual'
  const { isActive: isWakeLockActive } = useScreenWakeLock();

  // Use the tennis match data hook
  const matchData = useTennisMatchData(matchId)

  // Add tiebreaker state
  const [tiebreakerTarget, setTiebreakerTarget] = useState(7)
  const [tiebreakerWinByTwo, setTiebreakerWinByTwo] = useState(true)
  const [isSuperTiebreak, setIsSuperTiebreak] = useState(false)

  // Use the tiebreak logic hook
  const {
    tiebreakPoints: hookTiebreakPoints,
    isTiebreakActive,
    setIsTiebreakActive,
    incrementPoint,
    resetTiebreak,
  } = useTiebreakLogic(matchId, tiebreakerTarget) // Use dynamic tiebreakerTarget

  // Local tiebreak points state to ensure immediate updates
  const [localTiebreakPoints, setLocalTiebreakPoints] = useState({ team1: 0, team2: 0 })

  // Use the tennis match logic hook
  const {
    team1Games,
    team2Games,
    pendingSetCompletion,
    isQRCodeVisible,
    setQRCodeVisible,
    lastScoringTeam,
    handleCommand,
    handleSetCompletion,
    handleGameReset
  } = useTennisMatchLogic({
    matchId,
    team1Color,
    team2Color,
    isController,
    showQRCode
  })

  // State variables with Tennis prefix (keeping tennis-specific state not covered by hook)
  const [tennisSetScores, setTennisSetScores] = useState<{ team1: number; team2: number; tiebreakScore?: { team1: number; team2: number } }[]>([])
  const [tennisIsMuted, setTennisIsMuted] = useState(false)
  const [tennisShowQRCodeModal, setTennisShowQRCodeModal] = useState(false)
  const [tennisShowMicrophoneSettings, setTennisShowMicrophoneSettings] = useState(false)
  const [tennisShowStatistics, setTennisShowStatistics] = useState(false)
  const [tennisShowHelp, setTennisShowHelp] = useState(false)
  const [tennisShowExitConfirmation, setTennisShowExitConfirmation] = useState(false)
  const [tennisIsDarkTheme, setTennisIsDarkTheme] = useState(() => {
    const stored = localStorage.getItem('scoringThemeDark')
    return stored === 'true'
  })

  useEffect(() => {
    localStorage.setItem('scoringThemeDark', tennisIsDarkTheme.toString())
  }, [tennisIsDarkTheme])

  const [tennisTranscript, setTennisTranscript] = useState('')
  const [tennisLastError, setTennisLastError] = useState<string | null>(null)
  const [showIPhoneModal, setShowIPhoneModal] = useState(false)
  const [tennisControllerToken, setTennisControllerToken] = useState<string | null>(null)
  const [tennisCooldown, setTennisCooldown] = useState(0)
  const [tennisLastScoringTeam, setTennisLastScoringTeam] = useState<'team1' | 'team2' | null>(null)
  const [tennisPendingReset, setTennisPendingReset] = useState<{ timer: number } | null>(null)
  const [isKicked, setIsKicked] = useState(false)
  
  // Auto-fullscreen on Chrome
  const { toggleFullscreen: toggleTennisFullscreen } = useAutoFullscreenChrome()

  const [currentControllerNumber, setCurrentControllerNumber] = useState<number | null>(null)
  const localControllerNumber = useRef<number | null>(null)
  const [showTiebreakNotification, setShowTiebreakNotification] = useState(false)
  const isClearingTiebreakNotification = useRef(false)
  const hasShownTiebreakNotification = useRef(false)

  // Refs for managing recognition and intervals
  const tennisIsInCooldown = useRef(false)
  const tennisHelpRef = useRef<HTMLDivElement>(null)
  const tennisResetTimerRef = useRef<NodeJS.Timeout | null>(null)
  const tennisGameCompletionTimerRef = useRef<NodeJS.Timeout | null>(null)
  const tennisTiebreakCompletionTimerRef = useRef<NodeJS.Timeout | null>(null)
  const pendingResetRef = useRef<{ timer: number } | null>(null)

  const toast = useToast()

  // Generate controller token for tennis matches
  useEffect(() => {
    // Only show for iOS devices (iPad and iPhone)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    setShowIPhoneModal(isIOS)

    if (!isController || !matchId) return

    const handleTennisToken = async () => {
      try {
        // Check if there's an existing token in the URL
        const urlParams = new URLSearchParams(window.location.search)
        const existingToken = urlParams.get('token')
        const isControllerParam = urlParams.get('controller') === 'true'
        
        if (existingToken && isControllerParam) {
          // Check if this token is already being used by another user
          const controllerRef = ref(database, `matches/${matchId}/controllers/${existingToken}`)
          const controllerSnapshot = await get(controllerRef)
          
          if (controllerSnapshot.exists()) {
            const controllerData = controllerSnapshot.val()
            const tokenAge = Date.now() - controllerData.createdAt
            
            // If token is older than 5 seconds or was accessed via QR code, generate a new one
            if (tokenAge > 5000 || window.performance.navigation.type === 0) {
              // Generate new token
              const newToken = crypto.randomUUID()
              const newControllerRef = ref(database, `matches/${matchId}/controllers/${newToken}`)
              
              // Get next available controller number
              const controllersRef = ref(database, `matches/${matchId}/controllers`)
              const controllersSnapshot = await get(controllersRef)
              let nextControllerNumber = 1
              
              if (controllersSnapshot.exists()) {
                const controllers = controllersSnapshot.val()
                const usedNumbers = Object.values(controllers).map((c: any) => c.controllerNumber)
                while (usedNumbers.includes(nextControllerNumber)) {
                  nextControllerNumber++
                }
              }
              
              // Create new controller entry
              await set(newControllerRef, {
                createdAt: Date.now(),
                expiresAt: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
                isActive: true,
                permissions: ['score', 'control'],
                controllerNumber: nextControllerNumber
              })
              
              // Update URL with new token without causing refresh
              const newUrl = new URL(window.location.href)
              newUrl.searchParams.set('token', newToken)
              window.history.replaceState({}, '', newUrl.toString())
              
              // Update local state
              localControllerNumber.current = nextControllerNumber
              setCurrentControllerNumber(nextControllerNumber)
              setTennisControllerToken(newToken)
              return
            } else {
              // This is a fresh token, use it
              const controllerNumber = controllerData.controllerNumber || 1
              
              // Store controller number locally for reliable checking
              localControllerNumber.current = controllerNumber
              setCurrentControllerNumber(controllerNumber)
              setTennisControllerToken(existingToken)
              return
            }
          }
        }
        
              // Check if user is already the host controller before generating new token
      const controllersRef = ref(database, `matches/${matchId}/controllers`)
      const controllersSnapshot = await get(controllersRef)
      
      if (controllersSnapshot.exists()) {
        const controllers = controllersSnapshot.val()
        const controllerEntries = Object.entries(controllers)
        
        // Find the host controller (controller #1)
        const hostControllerEntry = controllerEntries.find(([token, data]: [string, any]) => {
          return data.controllerNumber === 1
        })
        
        if (hostControllerEntry) {
          const [hostToken, hostData] = hostControllerEntry as [string, any]
          
          // Check if this user should use the host token
          // If there's an existing token in URL and it matches the host token, use it
          if (existingToken === hostToken) {
            // Store controller number locally for reliable checking
            localControllerNumber.current = hostData.controllerNumber
            setCurrentControllerNumber(hostData.controllerNumber)
            setTennisControllerToken(hostToken)
            return
          }
          
          // If no existing token in URL and this is the first controller, use the host token
          if (!existingToken && controllerEntries.length === 1) {
            // Store controller number locally for reliable checking
            localControllerNumber.current = hostData.controllerNumber
            setCurrentControllerNumber(hostData.controllerNumber)
            setTennisControllerToken(hostToken)
            return
          }
        }
      }
      
      // Generate new token if no existing token, token doesn't exist, or it's a shared token
      const newToken = crypto.randomUUID()
      const controllerRef = ref(database, `matches/${matchId}/controllers/${newToken}`)
      
      // Get existing controllers to assign controller number
      let controllerNumber = 1
      
      if (controllersSnapshot.exists()) {
        const controllers = controllersSnapshot.val()
        const existingNumbers = Object.values(controllers).map((c: any) => c.controllerNumber || 0)
        controllerNumber = Math.max(...existingNumbers, 0) + 1
      }
      
      await set(controllerRef, {
        createdAt: Date.now(),
        expiresAt: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
        isActive: true,
        permissions: ['score', 'control'],
        controllerNumber: controllerNumber
      })

      // Store controller number locally for reliable checking
      localControllerNumber.current = controllerNumber
      setCurrentControllerNumber(controllerNumber)
      setTennisControllerToken(newToken)
        
      } catch (error) {
        console.error('Tennis token generation error:', error)
      }
    }

    handleTennisToken()
  }, [matchId, isController])

  // Constants with Tennis prefix
  const TENNIS_VALID_COMMANDS = {
    POINT: 'point',
    REVERSE_POINT: 'reverse point',
    RESET_MATCH: 'reset match',
    CONFIRM: 'confirm',
    CANCEL: 'cancel'
  } as const

  // Add state for game completion
  const [tennisPendingGameCompletion, setTennisPendingGameCompletion] = useState<{
    team: 'team1' | 'team2'
    timer: number
    finalScore: { team1: number; team2: number }
  } | null>(null)
  const [tennisGameCompletionTimer, setTennisGameCompletionTimer] = useState(8)
  const tennisGameCompletionCancelledRef = useRef(false)
  const lastPointCommandTimeRef = useRef(0)
  const tennisGameCompletionActiveRef = useRef(false)
  const tennisGameCompletionInProgressRef = useRef(false) // Add this ref to prevent duplicate calls

  // Add state for tie-break completion
  const [tennisPendingTiebreakCompletion, setTennisPendingTiebreakCompletion] = useState<{
    team: 'team1' | 'team2'
    timer: number
    finalScore: { team1: number; team2: number }
  } | null>(null)
  const [tennisTiebreakCompletionTimer, setTennisTiebreakCompletionTimer] = useState(6)
  const tennisTiebreakCompletionCancelledRef = useRef(false)
  const lastTiebreakPointCommandTimeRef = useRef(0)
  const tennisTiebreakCompletionActiveRef = useRef(false)
  const tennisTiebreakCompletionInProgressRef = useRef(false) // Add this ref to prevent duplicate calls

  // Add state for set completion
  const [tennisPendingSetCompletion, setTennisPendingSetCompletion] = useState<{
    team: 'team1' | 'team2'
    timer: number
  } | null>(null)
  const [tennisSetCompletionTimer, setTennisSetCompletionTimer] = useState(6)
  const tennisSetCompletionCancelledRef = useRef(false)
  const tennisSetCompletionActiveRef = useRef(false)
  const tennisSetCompletionInProgressRef = useRef(false)
  const lastSuccessfulCommandTimeRef = useRef(Date.now())

  // On mount, get tiebreaker settings from match data
  useEffect(() => {
    getMatch(matchId).then(matchData => {
      if (matchData) {
        // Check if this should be a Super tie-breaker (3rd set, 1-1 score, 10-point setting)
        const currentSetScores = matchData.setScores || []
        const isThirdSet = currentSetScores.length === 2 // 0-indexed, so 2 means 3rd set
        const isScoreOneOne = currentSetScores.length === 2 && 
          ((currentSetScores[0].team1 > currentSetScores[0].team2 && currentSetScores[1].team2 > currentSetScores[1].team1) ||
           (currentSetScores[0].team2 > currentSetScores[0].team1 && currentSetScores[1].team1 > currentSetScores[1].team2))
        const isSuperTiebreakSetting = matchData.tiebreakerType === 'superTie10'
        
        if (isThirdSet && isScoreOneOne && isSuperTiebreakSetting) {
          // This is a Super tie-breaker (3rd set, 1-1 score, 10-point setting)
          setIsSuperTiebreak(true)
          setTiebreakerTarget(10)
        } else {
          // Regular tie-breaker
          setIsSuperTiebreak(false)
          setTiebreakerTarget(7)
        }
        
        setTiebreakerWinByTwo(matchData.winByTwo)
      }
    })
  }, [matchId, matchData?.setScores, matchData?.tiebreakerType])

  // Detect tiebreaker start
  useEffect(() => {
    const checkTiebreakStart = async () => {
      const matchData = await getMatch(matchId)
      if (!matchData) return
      const gamesPerSet = matchData.pointsPerSet || 6
      const team1Games = matchData.gameScores?.filter((score: { team1: number; team2: number }) => score.team1 > score.team2).length || 0
      const team2Games = matchData.gameScores?.filter((score: { team1: number; team2: number }) => score.team2 > score.team1).length || 0
      const currentSetScores = matchData.setScores || []
      const isThirdSet = currentSetScores.length === 2 // 0-indexed, so 2 means 3rd set
      const isScoreOneOne = currentSetScores.length === 2 && 
        ((currentSetScores[0].team1 > currentSetScores[0].team2 && currentSetScores[1].team2 > currentSetScores[1].team1) ||
         (currentSetScores[0].team2 > currentSetScores[0].team1 && currentSetScores[1].team1 > currentSetScores[1].team2))
      const isSuperTiebreakSetting = matchData.tiebreakerType === 'superTie10'
      
      // Check for Super tie-breaker (3rd set, 1-1 score, 10-point setting)
      // Trigger immediately when 3rd set starts (no games played yet in 3rd set)
      if (isThirdSet && isScoreOneOne && isSuperTiebreakSetting && team1Games === 0 && team2Games === 0 && !isTiebreakActive) {
        // Trigger Super tie-breaker immediately for the 3rd set
        const alreadyActive = matchData.isTiebreak;
        const existingTiebreakPoints = matchData.tiebreakPoints || { team1: 0, team2: 0 };
        if (!alreadyActive) {
          // Only set to 0-0 if both are 0, otherwise preserve existing
          const pointsAreZero = (existingTiebreakPoints.team1 === 0 && existingTiebreakPoints.team2 === 0);
          const matchRef = ref(database, `matches/${matchId}`)
          await update(matchRef, {
            isTiebreak: true,
            tiebreakPoints: pointsAreZero ? { team1: 0, team2: 0 } : existingTiebreakPoints,
            isSuperTiebreak: true, // Mark this as a Super tie-breaker
            tiebreakNotification: true // Trigger the notification popup
          })
        }
        setIsTiebreakActive(true)
        setIsSuperTiebreak(true)
        setTiebreakerTarget(10)
        return
      }
      
      // Regular tie-break logic (when both players reach the games per set value)
      if (!isTiebreakActive && team1Games === gamesPerSet && team2Games === gamesPerSet) {
        const alreadyActive = matchData.isTiebreak;
        const existingTiebreakPoints = matchData.tiebreakPoints || { team1: 0, team2: 0 };
        if (!alreadyActive) {
          // Only set to 0-0 if both are 0, otherwise preserve existing
          const pointsAreZero = (existingTiebreakPoints.team1 === 0 && existingTiebreakPoints.team2 === 0);
          const matchRef = ref(database, `matches/${matchId}`)
          await update(matchRef, {
            isTiebreak: true,
            tiebreakPoints: pointsAreZero ? { team1: 0, team2: 0 } : existingTiebreakPoints,
            isSuperTiebreak: false // Mark this as a regular tie-breaker
          })
        }
        setIsTiebreakActive(true)
        setIsSuperTiebreak(false)
        setTiebreakerTarget(7)
      }
    }
    checkTiebreakStart()
  }, [matchData?.gameScores, isTiebreakActive, matchId, matchData?.setScores, matchData?.tiebreakerType])

  // Sync tiebreak state with Firebase
  useEffect(() => {
    if (matchData?.isTiebreak !== undefined) {
      // If Firebase has isTiebreak: true but our local state doesn't, update it
      if (matchData.isTiebreak && !isTiebreakActive) {
        setIsTiebreakActive(true)
        hasShownTiebreakNotification.current = false // Reset flag when new tie-break starts
        // Check if this is a Super tie-breaker
        if (matchData.isSuperTiebreak) {
          setIsSuperTiebreak(true)
          setTiebreakerTarget(10)
        } else {
          setIsSuperTiebreak(false)
          setTiebreakerTarget(7)
        }
      }
      // If Firebase has isTiebreak: false but our local state is true, update it
      else if (!matchData.isTiebreak && isTiebreakActive) {
        setIsTiebreakActive(false)
        setIsSuperTiebreak(false)
        setTiebreakerTarget(7)
        hasShownTiebreakNotification.current = false // Reset the flag when tie-break ends
      }
    }
  }, [matchData?.isTiebreak, matchData?.isSuperTiebreak, isTiebreakActive])

  // Update local cooldown state from Firebase data
  useEffect(() => {
    if (matchData?.scores) {
      // Always set the lastScoringTeam first
      if (matchData.scores.lastScoringTeam !== undefined) {
        setTennisLastScoringTeam(matchData.scores.lastScoringTeam as 'team1' | 'team2' | null)
      }
      
      // Then handle the cooldown
      if (matchData.scores.cooldown !== undefined) {
        setTennisCooldown(matchData.scores.cooldown)
        
        // If cooldown is > 0, start the countdown timer immediately
        if (matchData.scores.cooldown > 0) {
          const timer = setInterval(() => {
            setTennisCooldown(prev => {
              if (prev <= 1) {
                clearInterval(timer)
                return 0
              }
              return prev - 1
            })
          }, 1000)

          return () => {
            clearInterval(timer)
          }
        }
      }
    }
  }, [matchData?.scores])

  // Sync pendingReset state with Firebase data
  useEffect(() => {
    if (matchData?.pendingReset !== undefined) {
      if (matchData.pendingReset) {
        // If Firebase has pendingReset but we don't, start the timer
        if (!pendingResetRef.current) {
          pendingResetRef.current = matchData.pendingReset
          setTennisPendingReset(matchData.pendingReset)
          
          // Start the timer countdown
          if (tennisResetTimerRef.current) {
            clearInterval(tennisResetTimerRef.current)
          }
          
          tennisResetTimerRef.current = setInterval(() => {
            setTennisPendingReset(prev => {
              if (!prev) return null
              const newTimer = prev.timer - 1
              if (newTimer <= 0) {
                if (tennisResetTimerRef.current) {
                  clearInterval(tennisResetTimerRef.current)
                  tennisResetTimerRef.current = null
                }
                pendingResetRef.current = null
                // Clear pendingReset in Firebase when timer expires
                const matchRef = ref(database, `matches/${matchId}`)
                          update(matchRef, { pendingReset: null }).then(() => {
          }).catch(error => {
                  console.error('❌ TennisScoringScreen: Error clearing Firebase pendingReset on timer expiration (from sync):', error)
                })
                return null
              }
              pendingResetRef.current = { timer: newTimer }
                        // Update timer in Firebase
          const matchRef = ref(database, `matches/${matchId}`)
          update(matchRef, { pendingReset: { timer: newTimer } }).then(() => {
          }).catch(error => {
                console.error('❌ TennisScoringScreen: Error updating Firebase timer (from sync):', error)
              })
              return { timer: newTimer }
            })
          }, 1000)
        } else {
          // Update our state to match Firebase
          setTennisPendingReset(matchData.pendingReset)
          pendingResetRef.current = matchData.pendingReset
        }
      } else {
        // Firebase has no pendingReset, clear our state
        if (tennisResetTimerRef.current) {
          clearInterval(tennisResetTimerRef.current)
          tennisResetTimerRef.current = null
        }
        pendingResetRef.current = null
        setTennisPendingReset(null)
      }
    }
  }, [matchData?.pendingReset])

  // Sync local tiebreak points with hook value from Firebase
  useEffect(() => {
    if (
      localTiebreakPoints.team1 !== hookTiebreakPoints.team1 ||
      localTiebreakPoints.team2 !== hookTiebreakPoints.team2
    ) {
      setLocalTiebreakPoints(hookTiebreakPoints);
    }
  }, [hookTiebreakPoints]);

  // Sync set scores from Firebase data
  useEffect(() => {
    // Always update set scores from Firebase (including empty array on reset)
    setTennisSetScores(matchData?.setScores || []);
  }, [matchData?.setScores]);

  // Sync pending game completion from Firebase data
  useEffect(() => {
    if (matchData?.pendingGameCompletion && typeof matchData.pendingGameCompletion === 'object' && 'team' in matchData.pendingGameCompletion && 'timer' in matchData.pendingGameCompletion) {
      setTennisPendingGameCompletion(matchData.pendingGameCompletion)
      setTennisGameCompletionTimer((matchData.pendingGameCompletion as { timer: number }).timer)
    } else {
      setTennisPendingGameCompletion(null)
      setTennisGameCompletionTimer(0)
    }
  }, [matchData?.pendingGameCompletion]);

  // Sync pending tiebreak completion from Firebase data
  useEffect(() => {
    if (matchData?.pendingTiebreakCompletion && typeof matchData.pendingTiebreakCompletion === 'object' && 'team' in matchData.pendingTiebreakCompletion && 'timer' in matchData.pendingTiebreakCompletion) {
      setTennisPendingTiebreakCompletion(matchData.pendingTiebreakCompletion)
      setTennisTiebreakCompletionTimer((matchData.pendingTiebreakCompletion as { timer: number }).timer)
    } else {
      setTennisPendingTiebreakCompletion(null)
      setTennisTiebreakCompletionTimer(0)
    }
  }, [matchData?.pendingTiebreakCompletion]);

  // Sync pending set completion from Firebase data
  useEffect(() => {
    if (matchData?.pendingSetCompletion && typeof matchData.pendingSetCompletion === 'object' && 'team' in matchData.pendingSetCompletion && 'timer' in matchData.pendingSetCompletion) {
      setTennisPendingSetCompletion(matchData.pendingSetCompletion)
      setTennisSetCompletionTimer((matchData.pendingSetCompletion as { timer: number }).timer)
    } else {
      setTennisPendingSetCompletion(null)
      setTennisSetCompletionTimer(0)
    }
  }, [matchData?.pendingSetCompletion]);

  // Debug Statistics component data
  useEffect(() => {
    if (tennisShowStatistics) {
      console.log('TennisScoringScreen - tennisSetScores being passed to Statistics:', tennisSetScores);
      console.log('TennisScoringScreen - matchData.setScores from Firebase:', matchData?.setScores);
    }
  }, [tennisShowStatistics, tennisSetScores, matchData?.setScores]);

  // Listen for tiebreakNotification in matchData
  useEffect(() => {
    if (matchData?.tiebreakNotification) {
      hasShownTiebreakNotification.current = true;
      setShowTiebreakNotification(true);
      const timer = setTimeout(() => {
        isClearingTiebreakNotification.current = true;
        setShowTiebreakNotification(false);
        // Clear the tiebreakNotification in Firebase so it doesn't trigger again
        const matchRef = ref(database, `matches/${matchId}`);
        update(matchRef, { tiebreakNotification: false });
        // Reset the clearing flag after a short delay
        setTimeout(() => {
          isClearingTiebreakNotification.current = false;
        }, 500);
      }, 5000); // 5 seconds
      return () => {
        clearTimeout(timer);
      };
    } else {
      setShowTiebreakNotification(false);
    }
  }, [matchData?.tiebreakNotification, matchId]);

  // When tiebreak starts, set tiebreakNotification in Firebase
  useEffect(() => {
    if (isTiebreakActive && matchData && !showTiebreakNotification && !isClearingTiebreakNotification.current && !matchData.tiebreakNotification && !hasShownTiebreakNotification.current) {
      const matchRef = ref(database, `matches/${matchId}`);
      update(matchRef, { tiebreakNotification: true });
    }
  }, [isTiebreakActive, matchData, matchId, showTiebreakNotification]);

  // Helper functions with Tennis prefix
  const showTennisToast = (title: string, description: string, status: 'info' | 'warning' | 'success' | 'error') => {
    toast({
      title,
      description,
      status,
      duration: 3000,
      isClosable: true,
    })
  }

  // Core functions with Tennis prefix
  const handleTennisCommand = async (command: string[]) => {
    if (!isController || isKicked) {
      return
    }

    // FAILSAFE: Clear stuck blocking refs if no successful command in 8.2+ seconds
    const timeSinceLastSuccess = Date.now() - lastSuccessfulCommandTimeRef.current
    if (timeSinceLastSuccess > 8200) {
      console.log('🔧 FAILSAFE: Clearing stuck blocking refs (no successful commands for 8.2+ seconds)')
      tennisGameCompletionActiveRef.current = false
      tennisGameCompletionInProgressRef.current = false
      tennisTiebreakCompletionActiveRef.current = false
      tennisTiebreakCompletionInProgressRef.current = false
      tennisSetCompletionActiveRef.current = false
      tennisSetCompletionInProgressRef.current = false
      tennisIsInCooldown.current = false
      // Don't clear Firebase state - only local refs
    }

    // Prevent all point-add commands if cooldown is active
    if ((tennisCooldown > 0 || tennisIsInCooldown.current) && !command.some((alt) => alt.toLowerCase().includes('cancel') || alt.toLowerCase().includes('confirm'))) {
      // Optionally, show a toast or log here if you want feedback
      return
    }

    // Check for cancel command FIRST (before any other processing)
    const isCancelCommand = command.some((alt: string) => 
      alt.toLowerCase().includes('cancel') || 
      alt.toLowerCase().includes('cancel set')
    )

    // Check for confirm command when there's a pending reset
    const isConfirmCommand = command.some((alt: string) => {
      const lowerAlt = alt.toLowerCase().trim()
      return lowerAlt === 'confirm' || lowerAlt === 'confirm reset' || lowerAlt === 'confirmed' || lowerAlt === 'confirms' || lowerAlt === 'confirming'
    })

    // Block all commands except "Cancel" and "Confirm" during cooldown
    if (!isCancelCommand && !isConfirmCommand && tennisIsInCooldown.current) {
      return
    }

    // Check if any completion is in progress
    if ((tennisPendingGameCompletion || tennisPendingTiebreakCompletion || tennisPendingSetCompletion || tennisGameCompletionActiveRef.current || tennisTiebreakCompletionActiveRef.current || tennisSetCompletionActiveRef.current) && !isCancelCommand) {
      console.log('❌ TennisScoringScreen: Command blocked - completion in progress')
      return
    }

    // Handle cancel commands
    if (isCancelCommand) {
      if (tennisPendingGameCompletion) {
        console.log('✅ TennisScoringScreen: Cancelling game completion')
        const matchRef = ref(database, `matches/${matchId}`)
        await update(matchRef, {
          pendingGameCompletion: null
        })
        setTennisPendingGameCompletion(null)
        // Clear all completion active refs when canceling
        tennisGameCompletionActiveRef.current = false
        tennisGameCompletionInProgressRef.current = false
        lastSuccessfulCommandTimeRef.current = Date.now()
        return
      }
      if (tennisPendingTiebreakCompletion) {
        console.log('✅ TennisScoringScreen: Cancelling tiebreak completion')
        const matchRef = ref(database, `matches/${matchId}`)
        await update(matchRef, {
          pendingTiebreakCompletion: null
        })
        setTennisPendingTiebreakCompletion(null)
        tennisTiebreakCompletionActiveRef.current = false
        lastSuccessfulCommandTimeRef.current = Date.now()
        return
      }
      if (tennisPendingSetCompletion) {
        console.log('✅ TennisScoringScreen: Cancelling set completion')
        const matchRef = ref(database, `matches/${matchId}`)
        await update(matchRef, {
          pendingSetCompletion: null
        })
        setTennisPendingSetCompletion(null)
        tennisSetCompletionActiveRef.current = false
        lastSuccessfulCommandTimeRef.current = Date.now()
        return
      }
      return
    }

    // Check for confirm command when there's a pending reset
    if (isConfirmCommand && (tennisPendingReset || pendingResetRef.current)) {
      handleTennisConfirmReset()
      return
    }

    // Check for team points - simplified since speech recognition hook handles all variations
    const isTeam1Point = command.some((alt: string) => {
      const lowerAlt = alt.toLowerCase().trim()
      
      // Check if command contains the team color + "point"
      const exactColorMatch = lowerAlt.includes(`${team1Color} point`) || lowerAlt.includes(`${team1Color}point`)
      
      // For red team, also check for variations
      if (team1Color === 'red') {
        return exactColorMatch || 
               lowerAlt.includes('read point') || 
               lowerAlt.includes('readpoint') ||
               lowerAlt.includes('reads point') ||
               lowerAlt.includes('tread point') ||
               lowerAlt.includes('treadpoint') ||
               lowerAlt.includes('treads point') ||
               lowerAlt.includes('trade point') ||
               lowerAlt.includes('tradepoint') ||
               lowerAlt.includes('trades point') ||
               lowerAlt.includes('rex point') ||
               lowerAlt.includes('rexpoint') ||
               lowerAlt.includes('head point') ||
               lowerAlt.includes('heads point') ||
               lowerAlt.includes('romeo point') ||
               lowerAlt.includes('romeopoint') ||
               lowerAlt.includes('romeos point') ||
               lowerAlt.includes('romeo\'s point')
      }
      
      // For blue team, also check for variations
      if (team1Color === 'blue') {
        return exactColorMatch || 
               lowerAlt.includes('bluepoint') ||
               lowerAlt.includes('view point') ||
               lowerAlt.includes('views point') ||
               lowerAlt.includes('viewpoint') ||
               lowerAlt.includes('flew point') || 
               lowerAlt.includes('flews point') ||
               lowerAlt.includes('flu point') ||
               lowerAlt.includes('flus point') ||
               lowerAlt.includes('through point') ||
               lowerAlt.includes('throughs point') ||
               lowerAlt.includes('true point') ||
               lowerAlt.includes('true\'s point') ||
               lowerAlt.includes('lose point') ||
               lowerAlt.includes('bravo point') ||
               lowerAlt.includes('bravos point') ||
               lowerAlt.includes('brother point') ||
               lowerAlt.includes('brothers point') ||
               lowerAlt.includes('you point') ||
               lowerAlt.includes('youpoint') ||
               lowerAlt.includes('three point') ||
               lowerAlt.includes('3.')
      }
      
      // For green team, also check for variations
      if (team1Color === 'green') {
        return exactColorMatch || 
               lowerAlt.includes('greenpoint') ||
               lowerAlt.includes('cream point') || 
               lowerAlt.includes('creampoint') ||
               lowerAlt.includes('creams point') ||
               lowerAlt.includes('seen point') ||
               lowerAlt.includes('seenpoint') ||
               lowerAlt.includes('seens point') ||
               lowerAlt.includes('seem point') ||
               lowerAlt.includes('seempoint') ||
               lowerAlt.includes('seems point') ||
               lowerAlt.includes('golf point') ||
               lowerAlt.includes('golfpoint') ||
               lowerAlt.includes('golfs point') ||
               lowerAlt.includes('screen point') ||
               lowerAlt.includes('screenpoint')
      }
      
      // For yellow team, also check for variations
      if (team1Color === 'yellow') {
        return exactColorMatch || 
               lowerAlt.includes('hello point') || 
               lowerAlt.includes('hellopoint') ||
               lowerAlt.includes('hello is point') ||
               lowerAlt.includes('hello\'s point') ||
               lowerAlt.includes('hellos point') ||
               lowerAlt.includes('yankee point') ||
               lowerAlt.includes('yankee\'s point') ||
               lowerAlt.includes('yankees point') ||
               lowerAlt.includes('yellow') ||
               lowerAlt.includes('hello')
      }
      
      // For orange team, also check for variations
      if (team1Color === 'orange') {
        return exactColorMatch || 
               lowerAlt.includes('oscar point') || 
               lowerAlt.includes('oscarpoint') ||
               lowerAlt.includes('oscars point') ||
               lowerAlt.includes('range point') ||
               lowerAlt.includes('rangepoint') ||
               lowerAlt.includes('ranges point')
      }
      
      // For purple team, also check for variations
      if (team1Color === 'purple') {
        return exactColorMatch || 
               lowerAlt.includes('charlie point') || 
               lowerAlt.includes('charliepoint') ||
               lowerAlt.includes('charlies point') ||
               lowerAlt.includes('charlie\'s point') ||
               lowerAlt.includes('triple point')
      }
      
      return exactColorMatch
    })

    const isTeam2Point = command.some((alt: string) => {
      const lowerAlt = alt.toLowerCase().trim()
      
      // Check if command contains the team color + "point"
      const exactColorMatch = lowerAlt.includes(`${team2Color} point`) || lowerAlt.includes(`${team2Color}point`)
      
      // For red team, also check for variations
      if (team2Color === 'red') {
        return exactColorMatch || 
               lowerAlt.includes('read point') || 
               lowerAlt.includes('readpoint') ||
               lowerAlt.includes('reads point') ||
               lowerAlt.includes('tread point') ||
               lowerAlt.includes('treadpoint') ||
               lowerAlt.includes('treads point') ||
               lowerAlt.includes('trade point') ||
               lowerAlt.includes('tradepoint') ||
               lowerAlt.includes('trades point') ||
               lowerAlt.includes('rex point') ||
               lowerAlt.includes('rexpoint') ||
               lowerAlt.includes('head point') ||
               lowerAlt.includes('heads point') ||
               lowerAlt.includes('romeo point') ||
               lowerAlt.includes('romeopoint') ||
               lowerAlt.includes('romeos point') ||
               lowerAlt.includes('romeo\'s point')
      }
      
      // For blue team, also check for variations
      if (team2Color === 'blue') {
        return exactColorMatch || 
               lowerAlt.includes('bluepoint') ||
               lowerAlt.includes('view point') ||
               lowerAlt.includes('views point') ||
               lowerAlt.includes('viewpoint') ||
               lowerAlt.includes('flew point') || 
               lowerAlt.includes('flews point') ||
               lowerAlt.includes('flu point') ||
               lowerAlt.includes('flus point') ||
               lowerAlt.includes('through point') ||
               lowerAlt.includes('throughs point') ||
               lowerAlt.includes('true point') ||
               lowerAlt.includes('true\'s point') ||
               lowerAlt.includes('lose point') ||
               lowerAlt.includes('bravo point') ||
               lowerAlt.includes('bravos point') ||
               lowerAlt.includes('brother point') ||
               lowerAlt.includes('brothers point') ||
               lowerAlt.includes('you point') ||
               lowerAlt.includes('youpoint') ||
               lowerAlt.includes('three point') ||
               lowerAlt.includes('3 point')
      }
      
      // For green team, also check for variations
      if (team2Color === 'green') {
        return exactColorMatch || 
               lowerAlt.includes('greenpoint') ||
               lowerAlt.includes('cream point') || 
               lowerAlt.includes('creampoint') ||
               lowerAlt.includes('creams point') ||
               lowerAlt.includes('seen point') ||
               lowerAlt.includes('seenpoint') ||
               lowerAlt.includes('seens point') ||
               lowerAlt.includes('seem point') ||
               lowerAlt.includes('seempoint') ||
               lowerAlt.includes('seems point') ||
               lowerAlt.includes('golf point') ||
               lowerAlt.includes('golfpoint') ||
               lowerAlt.includes('golfs point') ||
               lowerAlt.includes('screen point') ||
               lowerAlt.includes('screenpoint')
      }
      
      // For yellow team, also check for variations
      if (team2Color === 'yellow') {
        return exactColorMatch || 
               lowerAlt.includes('hello point') || 
               lowerAlt.includes('hellopoint') ||
               lowerAlt.includes('hello is point') ||
               lowerAlt.includes('hello\'s point') ||
               lowerAlt.includes('hellos point') ||
               lowerAlt.includes('yankee point') ||
               lowerAlt.includes('yankee\'s point') ||
               lowerAlt.includes('yankees point') ||
               lowerAlt.includes('yellow') ||
               lowerAlt.includes('hello')
      }
      
      // For orange team, also check for variations
      if (team2Color === 'orange') {
        return exactColorMatch || 
               lowerAlt.includes('oscar point') || 
               lowerAlt.includes('oscarpoint') ||
               lowerAlt.includes('oscars point') ||
               lowerAlt.includes('range point') ||
               lowerAlt.includes('rangepoint') ||
               lowerAlt.includes('ranges point') ||
               lowerAlt.includes('romeo point') ||
               lowerAlt.includes('romeopoint') ||
               lowerAlt.includes('romeos point') ||
               lowerAlt.includes('romeo\'s point') ||
               lowerAlt.includes('all point') ||
               lowerAlt.includes('on point') ||
               lowerAlt.includes('armed point') ||
               lowerAlt.includes('orange')
      }
      
      // For purple team, also check for variations
      if (team2Color === 'purple') {
        return exactColorMatch || 
               lowerAlt.includes('charlie point') || 
               lowerAlt.includes('charliepoint') ||
               lowerAlt.includes('charlies point') ||
               lowerAlt.includes('charlie\'s point') ||
               lowerAlt.includes('romeo point') ||
               lowerAlt.includes('romeopoint') ||
               lowerAlt.includes('romeos point') ||
               lowerAlt.includes('romeo\'s point')
      }
      
      return exactColorMatch
    })

    // Handle team point commands
    if (isTeam1Point || isTeam2Point) {
      const scoringTeam = isTeam1Point ? 'team1' : 'team2'
      lastPointCommandTimeRef.current = Date.now()
      
      // Check both local and server state for pending completions
      if (tennisPendingGameCompletion || matchData?.pendingGameCompletion || 
          tennisPendingTiebreakCompletion || matchData?.pendingTiebreakCompletion) {
        return
      }
      
      // Always check Firebase for tiebreak state
      if (isTiebreakActive) {
        lastTiebreakPointCommandTimeRef.current = Date.now()
        
        // Check if this point would complete the tie-break BEFORE adding it
        const currentTeam1TiebreakPoints = hookTiebreakPoints.team1
        const currentTeam2TiebreakPoints = hookTiebreakPoints.team2
        const newTeam1TiebreakPoints = scoringTeam === 'team1' ? currentTeam1TiebreakPoints + 1 : currentTeam1TiebreakPoints
        const newTeam2TiebreakPoints = scoringTeam === 'team2' ? currentTeam2TiebreakPoints + 1 : currentTeam2TiebreakPoints
        
        // Check if this point would complete the tie-break (reached target with 2-point lead)
        const wouldCompleteTiebreak = (newTeam1TiebreakPoints >= tiebreakerTarget || newTeam2TiebreakPoints >= tiebreakerTarget) && 
                                     Math.abs(newTeam1TiebreakPoints - newTeam2TiebreakPoints) >= 2
        
        if (wouldCompleteTiebreak) {
          // Check if a tie-break completion was recently cancelled
          if (tennisTiebreakCompletionCancelledRef.current) {
            return
          }

          // Set the ref immediately to block commands
          tennisTiebreakCompletionActiveRef.current = true
          
          // Update Firebase with pending tiebreak completion
          const matchRef = ref(database, `matches/${matchId}`)
          await update(matchRef, {
            pendingTiebreakCompletion: {
              team: scoringTeam,
              timer: 6
            }
          })
          
          // Don't add the point yet - wait for timer or cancel
          return
        }
        
        try {
          await updateTiebreakPointWithCooldown(matchId, scoringTeam)
          lastSuccessfulCommandTimeRef.current = Date.now()
        } catch (error: any) {
          if (error.message && error.message.includes('cooldown')) {
            // Cooldown is handled by the progress bar, no need for toast
          } else {
            showTennisToast('Error', 'Failed to update tiebreak score. Please try again.', 'error')
          }
        }
        return
      }
      
      try {
        // Fetch the latest match data from Firebase first
        const matchData = await getMatch(matchId)
        if (!matchData) {
          console.error('Failed to fetch latest match data')
          return
        }

        if (!matchData.scores) {
          console.error('Failed to fetch latest scores')
          return
        }

        const latestTeam1Score = matchData.scores.team1 || 0
        const latestTeam2Score = matchData.scores.team2 || 0

        // Calculate new scores
        const newTeam1Score = scoringTeam === 'team1' ? latestTeam1Score + 1 : latestTeam1Score
        const newTeam2Score = scoringTeam === 'team2' ? latestTeam2Score + 1 : latestTeam2Score

        // Check if this point would complete the game BEFORE adding it
        const wouldCompleteGame = await checkGameCompletion(newTeam1Score, newTeam2Score)
        
        if (wouldCompleteGame) {
          // Check if a game completion was recently cancelled
          if (tennisGameCompletionCancelledRef.current) {
            return
          }

          // Set the ref immediately to block commands
          tennisGameCompletionActiveRef.current = true
          
          // Update Firebase with pending game completion
          const matchRef = ref(database, `matches/${matchId}`)
          await update(matchRef, {
            pendingGameCompletion: {
              team: scoringTeam,
              timer: 8
            }
          })

          lastSuccessfulCommandTimeRef.current = Date.now()

          // Don't add the point yet - wait for timer or cancel
          return
        }

        // If game would not be complete, add the point normally
        await updateScore(matchId, newTeam1Score, newTeam2Score, scoringTeam)
        await addScoreEvent(matchId, scoringTeam, 1)
        lastSuccessfulCommandTimeRef.current = Date.now()
        
      } catch (error: any) {
        console.error('Error updating score:', error)
        
        // Handle server-side cooldown errors
        if (error.message && error.message.includes('cooldown remaining')) {
          // Cooldown is handled by the progress bar, no need for toast
        } else {
          // showTennisToast('Error', 'Failed to update score. Please try again.', 'error')
        }
      }
      return
    }

    // Check for reverse point command
    const isReversePoint = command.some((alt: string) => {
      const lowerAlt = alt.toLowerCase().trim()
      return lowerAlt === 'reverse point' ||
             lowerAlt === 'reverse a point' ||
             lowerAlt === 'reverse one point' ||
             lowerAlt === 'reversepoint' ||
             lowerAlt === 'verse point' ||
             lowerAlt === 'versepoint' ||
             lowerAlt === 'vs point' ||
             lowerAlt === 'vspoint'
    })

    // Handle reverse point command
    if (isReversePoint) {
      try {
        // Check if we're in a tie-break
        if (isTiebreakActive) {
          // Handle tie-break point reversal
          const currentTeam1TiebreakPoints = hookTiebreakPoints.team1
          const currentTeam2TiebreakPoints = hookTiebreakPoints.team2
          
          // Check if both tie-break scores are 0
          if (currentTeam1TiebreakPoints === 0 && currentTeam2TiebreakPoints === 0) {
            return
          }
          
          // Determine which team scored last and reverse their point
          const matchData = await getMatch(matchId)
          if (!matchData) {
            console.error('Failed to fetch latest match data')
            return
          }
          
          // Get the last scoring team from Firebase
          const lastScoringTeam = matchData.scores?.lastScoringTeam
          
          if (!lastScoringTeam) {
            return
          }
          
          // Calculate new tie-break points
          const newTiebreakPoints = {
            team1: lastScoringTeam === 'team1' ? currentTeam1TiebreakPoints - 1 : currentTeam1TiebreakPoints,
            team2: lastScoringTeam === 'team2' ? currentTeam2TiebreakPoints - 1 : currentTeam2TiebreakPoints
          }
          
          // Ensure points don't go below 0
          if (newTiebreakPoints.team1 < 0 || newTiebreakPoints.team2 < 0) {
            return
          }
          
          // For tie-breaks, we need to track the previous scoring team for multiple reverses
          // Get the events to find the previous scoring team
          const events = matchData.events || {}
          const eventKeys = Object.keys(events)
          
          let previousScoringTeam: 'team1' | 'team2' | null = null
          
          if (eventKeys.length > 1) {
            // Get the second-to-last event to find the previous scoring team
            const secondLastEventKey = eventKeys[eventKeys.length - 2]
            const secondLastEvent = events[secondLastEventKey]
            previousScoringTeam = secondLastEvent.team
          }
          
          // Update tie-break points in Firebase
          const matchRef = ref(database, `matches/${matchId}`)
          await update(matchRef, {
            tiebreakPoints: newTiebreakPoints,
            'scores/lastScoringTeam': previousScoringTeam, // Set to previous scoring team instead of null
            'scores/cooldown': 0
          })
          
          // Force the tie-break hook to sync by clearing its refs
          // This is a workaround since the hook only updates on "newer" points
          const hookRef = (incrementPoint as any).__hookRef
          if (hookRef?.lastLocalUpdateRef) {
            hookRef.lastLocalUpdateRef.current = null
          }
          if (hookRef?.ignoreFirebaseUntil) {
            hookRef.ignoreFirebaseUntil.current = 0
          }
          
          // Also update local state directly for immediate UI update
          setLocalTiebreakPoints(newTiebreakPoints)
          
        } else {
          // Handle regular game point reversal
          // Fetch the latest match data from Firebase first
          const matchData = await getMatch(matchId)
          if (!matchData) {
            console.error('Failed to fetch latest match data')
            return
          }

          if (!matchData.scores) {
            console.error('Failed to fetch latest scores')
            return
          }

          const latestTeam1Score = matchData.scores.team1 || 0
          const latestTeam2Score = matchData.scores.team2 || 0

          // Check if both scores are 0
          if (latestTeam1Score === 0 && latestTeam2Score === 0) {
            return
          }

          await undoLastScore(matchId)
          lastSuccessfulCommandTimeRef.current = Date.now()
        }
      } catch (error: any) {
        console.error('Error reversing score:', error)
        
        // Handle server-side cooldown errors
        if (error.message && error.message.includes('cooldown remaining')) {
          // Cooldown is handled by the progress bar, no need for toast
        } else {
          showTennisToast('Error', 'Please wait 2 seconds between reverse operations', 'error')
        }
      }
      return
    }

    // Check for reset match command
    const isResetMatch = command.some((alt: string) => {
      const lowerAlt = alt.toLowerCase().trim()
      return lowerAlt === 'reset match' ||
             lowerAlt === 'resetmatch' ||
             lowerAlt === 'reset mach' ||
             lowerAlt === 'resetmach' ||
             lowerAlt === 'reset mac' ||
             lowerAlt === 'resetmac' ||
             lowerAlt === 'reset much' ||
             lowerAlt === 'resetmuch' ||
             lowerAlt === 'reset mush' ||
             lowerAlt === 'resetmush' ||
             lowerAlt === 'reset mash' ||
             lowerAlt === 'resetmash' ||
             lowerAlt === 'reset math' ||
             lowerAlt === 'resetmath' ||
             lowerAlt === 'reset max' ||
             lowerAlt === 'resetmax' ||
             lowerAlt === 'recent match' ||
             lowerAlt === 'recentmatch'
    })

    // Handle reset match command
    if (isResetMatch) {
      handleTennisResetMatch()
      return
    }

    // If no point-level commands matched, pass to the game-level logic
    handleCommand(command)
  }

  // Inside TennisScoringScreen component:
  const handleTennisCommandRef = useRef(handleTennisCommand);
  useEffect(() => {
    handleTennisCommandRef.current = handleTennisCommand;
  }, [handleTennisCommand]);

  // Pass a stable function to the speech recognition hook that always calls the latest ref value
  const { transcript, error, hasNetworkError } = useSpeechRecognitionController({
    handleCommand: useCallback((args: string[]) => {
      handleTennisCommandRef.current(args)
    }, []),
    isController,
    team1Color,
    team2Color,
  });

  // Use the hook's functions for reset operations
  const handleTennisResetMatch = () => {
    // Clear any existing timer first
    if (tennisResetTimerRef.current) {
      clearInterval(tennisResetTimerRef.current)
      tennisResetTimerRef.current = null
    }

    // Set the initial state
    const initialTimer = 12
    pendingResetRef.current = { timer: initialTimer }
    setTennisPendingReset({ timer: initialTimer })

    // Update Firebase so all devices see the pending reset
    const matchRef = ref(database, `matches/${matchId}`)
    update(matchRef, {
      pendingReset: { timer: initialTimer }
    })

    // Start the timer countdown
    tennisResetTimerRef.current = setInterval(() => {
      setTennisPendingReset(prev => {
        if (!prev) return null
        const newTimer = prev.timer - 1
        if (newTimer <= 0) {
          if (tennisResetTimerRef.current) {
            clearInterval(tennisResetTimerRef.current)
            tennisResetTimerRef.current = null
          }
          pendingResetRef.current = null
          // Clear pendingReset in Firebase
          update(matchRef, { pendingReset: null })
          return null
        }
        pendingResetRef.current = { timer: newTimer }
        // Update timer in Firebase
        update(matchRef, { pendingReset: { timer: newTimer } })
        return { timer: newTimer }
      })
    }, 1000)
  }

  const handleTennisConfirmReset = async () => {
    if (!pendingResetRef.current) {
      return
    }

    try {
      // Clear the timer first
      if (tennisResetTimerRef.current) {
        clearInterval(tennisResetTimerRef.current)
        tennisResetTimerRef.current = null
      }

              // Clean up old controllers before resetting (10 minutes, except host)
        await cleanupOldControllers(matchId, 10) // 10 minutes

      // First update the set scores to ensure all devices get the update
      const matchRef = ref(database, `matches/${matchId}`)
      await update(matchRef, {
        setScores: [],  // Clear set scores first
        gameScores: [], // Clear game scores
        totalCompletedGames: 0, // Reset cumulative game counter
        scores: {
          team1: 0,
          team2: 0,
          updatedAt: Date.now()
        },
        pendingSetCompletion: null,
        pendingGameCompletion: null, // Clear pending game completion
        pendingTiebreakCompletion: null, // Clear pending tiebreak completion
        events: {},  // Clear all events
        pendingReset: null, // Clear pendingReset in Firebase
        isTiebreak: false,
        tiebreakPoints: { team1: 0, team2: 0 },
        isSuperTiebreak: false // Clear Super tie-breaker flag
      })
      
      // Reset local state after Firebase update
      setTennisSetScores([]) // Explicitly clear set scores
      setTennisCooldown(0)
      setTennisLastScoringTeam(null)
      setIsSuperTiebreak(false)
      setTiebreakerTarget(7)
      pendingResetRef.current = null
      setTennisPendingReset(null)
      lastSuccessfulCommandTimeRef.current = Date.now()
    } catch (error) {
      console.error('Error resetting match:', error)
      showTennisToast('Error', 'Failed to reset match. Please try again.', 'error')
    }
  }

  const handleTennisExit = () => {
    setTennisShowExitConfirmation(true)
    setTennisShowHelp(false)
  }

  const handleTennisConfirmExit = () => {
    // For the main device, navigate back to main page
    window.location.href = '/'
  }

  const handleTennisCancelExit = () => {
    setTennisShowExitConfirmation(false)
  }

  const toggleTennisMute = () => {
    setTennisIsMuted(!tennisIsMuted)
  }


  // Add game completion check
  const checkGameCompletion = async (team1Points: number, team2Points: number): Promise<boolean> => {
    try {
      const matchData = await getMatch(matchId)
      if (!matchData) return false

      // If Golden Point is enabled (winByTwo is false)
      if (!matchData.winByTwo) {
        // Game is complete when a player reaches 4 points
        return team1Points >= 4 || team2Points >= 4
      }

      // If both players have 3 or more points (deuce situation)
      if (team1Points >= 3 && team2Points >= 3) {
        // Game is complete when one player leads by 2 points
        return Math.abs(team1Points - team2Points) >= 2
      }
      
      // Normal game completion (win by 2 points)
      if (team1Points >= 4 && team1Points - team2Points >= 2) return true
      if (team2Points >= 4 && team2Points - team1Points >= 2) return true
      
      return false
    } catch (error) {
      console.error('Error checking game completion:', error)
      return false
    }
  }

  // Use the hook's function for game completion
  const handleGameCompletion = async (team: 'team1' | 'team2') => {
    handleSetCompletion()
  }

  // Add set completion check
  const checkSetCompletion = async (team1Games: number, team2Games: number): Promise<boolean> => {
    try {
      const matchData = await getMatch(matchId)
      if (!matchData) return false
      
      const gamesPerSet = matchData.pointsPerSet || 6 // Default to 6 if not set

      // If both players have reached games per set, the next game wins
      if (team1Games >= gamesPerSet && team2Games >= gamesPerSet) {
        // Check if this is the final game (one team has won the next game)
        return team1Games > gamesPerSet || team2Games > gamesPerSet
      }

      // Otherwise, must win by 2 games
      return (team1Games >= gamesPerSet && team1Games - team2Games >= 2) ||
             (team2Games >= gamesPerSet && team2Games - team1Games >= 2)
    } catch (error) {
      console.error('Error checking set completion:', error)
      return false
    }
  }

  // Handle tennis game completion with point (add the point first, then complete the game)
  const handleTennisGameCompletionWithPoint = async (winningTeam: 'team1' | 'team2', team1Score: number, team2Score: number) => {
    try {
      // Check if already in progress to prevent duplicate calls
      if (tennisGameCompletionInProgressRef.current) {
        return;
      }
      
      // Set the flag to prevent duplicate calls
      tennisGameCompletionInProgressRef.current = true;
      
      // Get current match data
      const matchData = await getMatch(matchId)
      if (!matchData) {
        tennisGameCompletionInProgressRef.current = false; // Reset flag on error
        return
      }
                
      // Get current game scores
      const currentGameScores = matchData.gameScores || []
      
      // Add the final point event for statistics tracking
      await addScoreEvent(matchId, winningTeam, 1, 'game')
      
      // Add the completed game to the game scores
      const newGameScore = { 
        team1: winningTeam === 'team1' ? 1 : 0, 
        team2: winningTeam === 'team2' ? 1 : 0 
      }
      const updatedGameScores = [...currentGameScores, newGameScore]
      
      // Calculate new game counts
      const newTeam1Games = updatedGameScores.filter((score: { team1: number; team2: number }) => score.team1 > score.team2).length
      const newTeam2Games = updatedGameScores.filter((score: { team1: number; team2: number }) => score.team2 > score.team1).length
      
      // Check if this game completion also completes the set
      const isSetComplete = await checkSetCompletion(newTeam1Games, newTeam2Games)
      
      if (isSetComplete) {
        // Complete the set
        const currentSetScores = matchData.setScores || []
        const newSetScore = { 
          team1: newTeam1Games, 
          team2: newTeam2Games 
        }
        
        // Update Firebase with set completion
        const matchRef = ref(database, `matches/${matchId}`)
        await update(matchRef, {
          setScores: [...currentSetScores, newSetScore],
          gameScores: [], // Clear gameScores for the new set
          totalCompletedGames: (matchData.totalCompletedGames || 0) + 1, // Increment cumulative game counter
          scores: { team1: 0, team2: 0, updatedAt: Date.now(), cooldown: 0, lastScoringTeam: null },
          isTiebreak: false,
          tiebreakPoints: { team1: 0, team2: 0 },
          pendingGameCompletion: null // Clear pending game completion
        })
        
        // Update local set scores
        setTennisSetScores([...currentSetScores, newSetScore])
        
        // Show set completion overlay
        const matchRef2 = ref(database, `matches/${matchId}`)
        await update(matchRef2, {
          pendingSetCompletion: {
            team: winningTeam,
            timer: 6
          }
        })
      } else {
        // Just complete the game normally
        const matchRef = ref(database, `matches/${matchId}`)
        await update(matchRef, {
          gameScores: updatedGameScores,
          totalCompletedGames: (matchData.totalCompletedGames || 0) + 1, // Increment cumulative game counter
          scores: { team1: 0, team2: 0, updatedAt: Date.now(), cooldown: 0, lastScoringTeam: null },
          isTiebreak: false,
          tiebreakPoints: { team1: 0, team2: 0 },
          pendingGameCompletion: null // Clear pending game completion
        })
      }
      
      // Clear all completion flags and states
      setTennisPendingGameCompletion(null)
      setTennisPendingTiebreakCompletion(null)
      setTennisPendingSetCompletion(null)
      
      // Clear all active refs
      tennisGameCompletionActiveRef.current = false
      tennisTiebreakCompletionActiveRef.current = false
      tennisSetCompletionActiveRef.current = false
      
      // Reset all in-progress flags
      tennisGameCompletionInProgressRef.current = false
      tennisTiebreakCompletionInProgressRef.current = false
      tennisSetCompletionInProgressRef.current = false;
      
      lastSuccessfulCommandTimeRef.current = Date.now()
      
    } catch (error: any) {
      console.error('Error completing game with point:', error)
      
      // Clear all completion flags and states on error
      setTennisPendingGameCompletion(null)
      setTennisPendingTiebreakCompletion(null)
      setTennisPendingSetCompletion(null)
      
      // Clear all active refs
      tennisGameCompletionActiveRef.current = false
      tennisTiebreakCompletionActiveRef.current = false
      tennisSetCompletionActiveRef.current = false
      
      // Reset all in-progress flags
      tennisGameCompletionInProgressRef.current = false
      tennisTiebreakCompletionInProgressRef.current = false
      tennisSetCompletionInProgressRef.current = false
      
      // Handle server-side cooldown errors
      if (error.message && error.message.includes('cooldown remaining')) {
        // Cooldown is handled by the progress bar, no need for toast
      } else {
        showTennisToast('Error', 'Failed to complete game. Please try again.', 'error')
      }
    }
  }

  // Handle tennis tie-break completion with point (add the point first, then complete the tie-break)
  const handleTennisTiebreakCompletionWithPoint = async (winningTeam: 'team1' | 'team2', team1Score: number, team2Score: number) => {
    try {
      // Check if already in progress to prevent duplicate calls
      if (tennisTiebreakCompletionInProgressRef.current) {
        return;
      }
      
      // Set the flag to prevent duplicate calls
      tennisTiebreakCompletionInProgressRef.current = true;
      
      // Add the final tie-break point event for statistics tracking
      await addScoreEvent(matchId, winningTeam, 1, 'game')
      
      // Complete the tie-break set
      await completeTiebreakSet(winningTeam, team1Score, team2Score)
      
      // Clear the pending tie-break completion in Firebase
      const matchRef = ref(database, `matches/${matchId}`)
      await update(matchRef, {
        pendingTiebreakCompletion: null
      }).catch(error => {
        console.error('Error clearing pending tiebreak completion:', error)
      })
      
      // Clear the active ref
      tennisTiebreakCompletionActiveRef.current = false
      
      // Reset the flag after successful completion
      tennisTiebreakCompletionInProgressRef.current = false;
      
      lastSuccessfulCommandTimeRef.current = Date.now()
      
    } catch (error: any) {
      console.error('Error completing tie-break with point:', error)
      
      // Reset the flag on error
      tennisTiebreakCompletionInProgressRef.current = false;
      
      // Handle server-side cooldown errors
      if (error.message && error.message.includes('cooldown remaining')) {
        // Cooldown is handled by the progress bar, no need for toast
      } else {
        showTennisToast('Error', 'Failed to complete tie-break. Please try again.', 'error')
      }
    }
  }

  // Add cooldown effect
  useEffect(() => {
    if (matchData?.cooldown && matchData.cooldown > 0) {
      tennisIsInCooldown.current = true

      const timer = setInterval(() => {
        // The cooldown is managed by the hook, so we just need to track the local state
        if (matchData.cooldown <= 1) {
            clearInterval(timer)
            tennisIsInCooldown.current = false
        }
      }, 1000)

      return () => clearInterval(timer)
    }
  }, [matchData?.cooldown])

  // Add click-away-to-close for help menu
  useEffect(() => {
    if (!tennisShowHelp) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (tennisHelpRef.current && !tennisHelpRef.current.contains(event.target as Node)) {
        setTennisShowHelp(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tennisShowHelp]);

  // Keep local timer in sync with Firebase timer for all devices
  useEffect(() => {
    if (typeof tennisPendingGameCompletion?.timer === 'number') {
      setTennisGameCompletionTimer(tennisPendingGameCompletion.timer)
    }
  }, [tennisPendingGameCompletion?.timer])

  // Keep local tie-break completion timer in sync for all devices
  useEffect(() => {
    if (typeof tennisPendingTiebreakCompletion?.timer === 'number') {
      setTennisTiebreakCompletionTimer(tennisPendingTiebreakCompletion.timer)
    }
  }, [tennisPendingTiebreakCompletion?.timer])

  // Keep local set completion timer in sync for all devices
  useEffect(() => {
    if (typeof tennisPendingSetCompletion?.timer === 'number') {
      setTennisSetCompletionTimer(tennisPendingSetCompletion.timer)
    }
  }, [tennisPendingSetCompletion?.timer])

  // Add kicked controller detection
  useEffect(() => {
    if (!tennisControllerToken || !matchId) {
      return
    }

    const controllerRef = ref(database, `matches/${matchId}/controllers/${tennisControllerToken}`)
    
    // Initial check
    get(controllerRef).then(snapshot => {
      if (snapshot.exists()) {
        const controllerData = snapshot.val()
        if (controllerData.controllerNumber) {
          // Set controller number if available
          setCurrentControllerNumber(controllerData.controllerNumber)
        }
      }
    })

    // Listen for controller entry changes (kicked detection)
    // This should work for any user with a token, not just controllers
    const unsubscribe = onValue(controllerRef, (snapshot) => {
      if (!snapshot.exists()) {
        // Check if this is the host controller (controller #1)
        const checkIfHostController = async () => {
          try {
            const controllersRef = ref(database, `matches/${matchId}/controllers`)
            const controllersSnapshot = await get(controllersRef)
            
            if (controllersSnapshot.exists()) {
              const controllers = controllersSnapshot.val()
              const controllerEntries = Object.entries(controllers)
              
              // Find the host controller (controller #1)
              const hostControllerEntry = controllerEntries.find(([token, data]: [string, any]) => {
                return data.controllerNumber === 1
              })
              
              const hostControllerToken = hostControllerEntry?.[0]
              const isHostController = hostControllerToken === tennisControllerToken
              
              // NEVER show kicked modal for host controller
              if (!isHostController) {
                setIsKicked(true)
              } else {
                // Ensure host is never marked as kicked
                setIsKicked(false)
              }
            }
          } catch (error) {
            console.error('❌ Tennis error checking host controller:', error)
            // Fallback to old logic - but still protect host
            if (localControllerNumber.current !== 1) {
              setIsKicked(true)
            } else {
              // Ensure host is never marked as kicked
              setIsKicked(false)
            }
          }
        }
        
        checkIfHostController()
      }
    })

    return () => unsubscribe()
  }, [tennisControllerToken, matchId, currentControllerNumber])

  // Timer for game completion countdown
  useEffect(() => {
    if (tennisPendingGameCompletion) {
      const { team } = tennisPendingGameCompletion;
      const timer = setInterval(() => {
        setTennisGameCompletionTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Auto-complete the game if no cancel - add the point and complete the game
            const matchRef = ref(database, `matches/${matchId}`)
            update(matchRef, { pendingGameCompletion: null }).then(() => {
              // Check if the Firebase data was successfully cleared before proceeding
              getMatch(matchId).then(matchData => {
                if (!matchData?.pendingGameCompletion && matchData?.scores) {
                  const currentTeam1Score = matchData.scores.team1 || 0
                  const currentTeam2Score = matchData.scores.team2 || 0
                  const newTeam1Score = team === 'team1' ? currentTeam1Score + 1 : currentTeam1Score
                  const newTeam2Score = team === 'team2' ? currentTeam2Score + 1 : currentTeam2Score
                  handleTennisGameCompletionWithPoint(team, newTeam1Score, newTeam2Score)
                }
              })
            }).catch(error => {
              console.error('Error clearing Firebase pendingGameCompletion on timer expiration:', error)
            })
            
            // Clear all completion refs when timer naturally expires
            tennisGameCompletionActiveRef.current = false
            tennisGameCompletionInProgressRef.current = false
            
            return 0;
          }
          const newTimer = prev - 1;
          // Update timer in Firebase
          const matchRef = ref(database, `matches/${matchId}`)
          update(matchRef, { pendingGameCompletion: { team, timer: newTimer } })
          return newTimer;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [tennisPendingGameCompletion, matchId]);

  // Timer for tiebreak completion countdown
  useEffect(() => {
    if (tennisPendingTiebreakCompletion) {
      const { team } = tennisPendingTiebreakCompletion;
      const timer = setInterval(() => {
        setTennisTiebreakCompletionTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Auto-complete the tiebreak if no cancel - add the point and complete the tiebreak
            const matchRef = ref(database, `matches/${matchId}`)
            update(matchRef, { pendingTiebreakCompletion: null }).then(() => {
              // Check if the Firebase data was successfully cleared before proceeding
              getMatch(matchId).then(matchData => {
                if (!matchData?.pendingTiebreakCompletion && matchData?.tiebreakPoints) {
                  const currentTeam1TiebreakPoints = matchData.tiebreakPoints.team1 || 0
                  const currentTeam2TiebreakPoints = matchData.tiebreakPoints.team2 || 0
                  const newTeam1TiebreakPoints = team === 'team1' ? currentTeam1TiebreakPoints + 1 : currentTeam1TiebreakPoints
                  const newTeam2TiebreakPoints = team === 'team2' ? currentTeam2TiebreakPoints + 1 : currentTeam2TiebreakPoints
                  handleTennisTiebreakCompletionWithPoint(team, newTeam1TiebreakPoints, newTeam2TiebreakPoints)
                }
              })
            }).catch(error => {
              console.error('Error clearing Firebase pendingTiebreakCompletion on timer expiration:', error)
            })
            
            // Clear the active ref
            tennisTiebreakCompletionActiveRef.current = false
            
            return 0;
          }
          const newTimer = prev - 1;
          // Update timer in Firebase
          const matchRef = ref(database, `matches/${matchId}`)
          update(matchRef, { pendingTiebreakCompletion: { team, timer: newTimer } })
          return newTimer;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [tennisPendingTiebreakCompletion, matchId]);

  // Timer for set completion countdown
  useEffect(() => {
    if (tennisPendingSetCompletion) {
      const { team } = tennisPendingSetCompletion;
      const timer = setInterval(() => {
        setTennisSetCompletionTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            
            // Clear local state on all devices
            setTennisPendingSetCompletion(null)
            setTennisSetCompletionTimer(0)
            
            // Only the controller should update Firebase
            if (isController) {
              // Auto-complete the set if no cancel - just clear the pending state
              const matchRef = ref(database, `matches/${matchId}`)
              update(matchRef, { pendingSetCompletion: null }).then(() => {
                console.log('✅ TennisScoringScreen: Set completion timer expired, starting new set')
              }).catch(error => {
                console.error('Error clearing Firebase pendingSetCompletion on timer expiration:', error)
              })
            }
            
            // Clear the active ref
            tennisSetCompletionActiveRef.current = false
            
            return 0;
          }
          const newTimer = prev - 1;
          
          // Only the controller should update Firebase timer
          if (isController) {
            // Update timer in Firebase
            const matchRef = ref(database, `matches/${matchId}`)
            update(matchRef, { pendingSetCompletion: { team, timer: newTimer } })
          }
          
          return newTimer;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [tennisPendingSetCompletion, matchId, isController]);

  // Complete tiebreak set
  async function completeTiebreakSet(winningTeam: 'team1' | 'team2', team1Points: number, team2Points: number) {
    const matchData = await getMatch(matchId)
    if (!matchData) return
    const currentSetScores = matchData.setScores || []
    
    let newSetScore
    if (isSuperTiebreak) {
      // For Super tie-breaker (3rd set), record as 1-0 or 0-1
      if (winningTeam === 'team1') {
        newSetScore = { 
          team1: 1, 
          team2: 0,
          tiebreakScore: { team1: team1Points, team2: team2Points }
        }
      } else {
        newSetScore = { 
          team1: 0, 
          team2: 1,
          tiebreakScore: { team1: team1Points, team2: team2Points }
        }
      }
    } else {
      // For regular tie-breaker, use pointsPerSet to determine the base set score (usually 6)
      const pointsPerSet = matchData.pointsPerSet || 6
      if (winningTeam === 'team1') {
        newSetScore = { 
          team1: pointsPerSet + 1, 
          team2: pointsPerSet,
          tiebreakScore: { team1: team1Points, team2: team2Points }
        }
      } else {
        newSetScore = { 
          team1: pointsPerSet, 
          team2: pointsPerSet + 1,
          tiebreakScore: { team1: team1Points, team2: team2Points }
        }
      }
    }
    
    const matchRef = ref(database, `matches/${matchId}`)
    await update(matchRef, {
      setScores: [...currentSetScores, newSetScore],
      gameScores: [], // Clear gameScores for the new set
      totalCompletedGames: (matchData.totalCompletedGames || 0) + 1, // Increment cumulative game counter (tiebreak = 1 game)
      scores: { team1: 0, team2: 0, updatedAt: Date.now(), cooldown: 0, lastScoringTeam: null },
      isTiebreak: false,
      tiebreakPoints: { team1: 0, team2: 0 },
      isSuperTiebreak: false // Clear Super tie-breaker flag
    })
    setTennisSetScores([...currentSetScores, newSetScore])
    setIsSuperTiebreak(false)
    resetTiebreak()
  }

  // Cleanup old controller tokens
  const cleanupOldControllers = async (matchId: string, cutoffMinutes: number = 10) => {
    try {
      const controllersRef = ref(database, `matches/${matchId}/controllers`)
      const controllersSnapshot = await get(controllersRef)
      
      if (!controllersSnapshot.exists()) return
      
      const controllers = controllersSnapshot.val()
      const cutoffTime = Date.now() - (cutoffMinutes * 60 * 1000)
      const controllersToRemove: string[] = []
      
      // Find controllers older than cutoff time, EXCEPT controller #1 (host)
      Object.keys(controllers).forEach(token => {
        const controller = controllers[token]
        if (controller.createdAt < cutoffTime && controller.controllerNumber !== 1) {
          controllersToRemove.push(token)
        }
      })
      
      // Remove old controllers
      if (controllersToRemove.length > 0) {
        const updates: any = {}
        controllersToRemove.forEach(token => {
          updates[`matches/${matchId}/controllers/${token}`] = null
        })
        
        await update(ref(database), updates)
      }
    } catch (error) {
      console.error('Error cleaning up old controllers:', error)
    }
  }

  // For display, always use isTiebreakActiveInFirebase
  const isTiebreakActiveForDisplay = matchData?.isTiebreak || false;
  const displayTiebreakPoints = localTiebreakPoints;

  // Side-swapping logic: sides swap after every completed game (including across set boundaries)
  // Use totalCompletedGames counter which tracks cumulative games across all sets
  const isTeam1Serving = (matchData?.totalCompletedGames || 0) % 2 === 0

  return (
    <Box minH="100vh" display="flex" flexDirection="column" bg="#09090b" overflow="hidden" color="white">
      <IPhoneModal isOpen={showIPhoneModal} onClose={() => setShowIPhoneModal(false)} />
      <InternetConnectionModal speechNetworkError={hasNetworkError} />
      {/* Menu items */}
      <Box 
        p={4} 
        minH="auto" 
        position="fixed"
        top={0}
        left={0}
        right={0}
        bg="#09090b"
        zIndex={999}
      >
        <TopBar
          transcript={transcript}
          error={error}
          onOpenMicSettings={() => setTennisShowMicrophoneSettings(true)}
          onOpenQRModal={() => setTennisShowQRCodeModal(true)}
          onOpenExitModal={() => setTennisShowExitConfirmation(true)}
          onOpenStatistics={() => setTennisShowStatistics(true)}
          onToggleFullscreen={toggleTennisFullscreen}
        />
      </Box>

      {/* QR Code Modal */}
      <QRCodeModal
        isOpen={tennisShowQRCodeModal}
        onClose={() => setTennisShowQRCodeModal(false)}
        matchId={matchId}
        isController={isController}
      />

      {/* Kicked Controller Message */}
      {isKicked && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="#09090b"
          zIndex={999999}
          display="flex"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          color="white"
          onClick={() => window.location.href = '/'}
          cursor="pointer"
        >
          <Box p={8}>
            <Text fontSize="xl" fontWeight="bold" mb={4} color="#3182ce">
              Thanks for playing!
            </Text>
            <Text mb={4}>
              The match has been reset, please re-join to control the score.
            </Text>
            <Button
              colorScheme="blue"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                window.location.href = '/';
              }}
              color="#3182ce"
              borderColor="#3182ce"
              _hover={{ bg: 'rgba(49, 130, 206, 0.1)' }}
            >
              Back
            </Button>
          </Box>
        </Box>
      )}

      {/* Exit Confirmation */}
      <ExitModal
        isOpen={tennisShowExitConfirmation}
        onClose={() => setTennisShowExitConfirmation(false)}
        onConfirm={() => {
          setTennisShowExitConfirmation(false)
          window.location.href = '/'
        }}
      />

      {/* Reset Confirmation Popup */}
      {tennisPendingReset && (
        <Box
          position="fixed"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          bg="rgba(0, 0, 0, 0.9)"
          p={8}
          borderRadius="lg"
          zIndex={1000}
          textAlign="center"
          width="300px"
        >
          <Text color="white" fontSize="xl" mb={4}>
            To reset say "<Button 
              variant="link" 
              color="white" 
              textDecoration="underline" 
              fontSize="xl" 
              fontWeight="normal"
              onClick={handleTennisConfirmReset}
            >Confirm</Button>"
          </Text>
          <Text color="white" fontSize="6xl" fontWeight="bold">
            {tennisPendingReset.timer}
          </Text>
          <Text color="white" fontSize="sm" opacity={0.8}>
            Dis-connects old controllers
          </Text>
          <Text color="white" fontSize="sm" opacity={0.8}>
                      (joined &gt;10min ago)
        </Text>
        </Box>
      )}

      {/* Main scoring display */}
      <VStack spacing={8} flex={1} justify="start" mt="60px" mb="32px">
        {/* Set Scores Display */}
        <VStack spacing={4} w="100%" justify="center" align="center" px={4} mt={2}>
          <HStack spacing={4} w="100%" justify="center" align="center" flexWrap="nowrap">
            {tennisSetScores.map((setScore, index) => (
              <Text 
                key={index}
                fontSize={{ 
                  base: "2xl",
                  md: "3xl"
                }} 
                color="white"
                whiteSpace="nowrap"
                fontWeight="bold"
              >
                Set {index + 1}:{' '}
                <Text as="span" color={getHexFromColor(team1Color)}>{setScore.team1}</Text>
                {'-'}
                <Text as="span" color={getHexFromColor(team2Color)}>{setScore.team2}</Text>
                {setScore.tiebreakScore && (
                  <Text as="span" color="gray.400" fontSize="sm">
                    {' '}({setScore.tiebreakScore.team1}-{setScore.tiebreakScore.team2})
                  </Text>
                )}
              </Text>
            ))}
            {/* Always show current set score */}
            <Text 
              fontSize={{ 
                base: "5xl",
                md: "6xl"
              }} 
              fontWeight="bold"
              color="white"
              whiteSpace="nowrap"
            >
              Set {tennisSetScores.length + 1}:{' '}
              <Text as="span" color={getHexFromColor(team1Color)}>
                {(matchData?.gameScores || []).filter((score: { team1: number; team2: number }) => score.team1 > score.team2).length}
              </Text>
              {'-'}
              <Text as="span" color={getHexFromColor(team2Color)}>
                {(matchData?.gameScores || []).filter((score: { team1: number; team2: number }) => score.team2 > score.team1).length}
              </Text>
              {isTiebreakActiveForDisplay && (
                <Text as="span" color="gray.400" fontSize="sm">
                  {' '}({displayTiebreakPoints.team1}-{displayTiebreakPoints.team2})
                </Text>
              )}
            </Text>
          </HStack>
        </VStack>

        <Flex
          w="100%"
          justify="center"
          align="stretch"
          gap={8}
        >
          {isTeam1Serving ? (
            <>
              <TennisScoreBox
                score={isTiebreakActiveForDisplay ? displayTiebreakPoints.team1.toString() : getDisplayScores(matchData?.scores?.team1 || 0, matchData?.scores?.team2 || 0).score1}
                name={team1Name}
                color={getHexFromColor(team1Color)}
                dark={tennisIsDarkTheme}
                onClick={isController ? () => handleTennisCommand([`${team1Color} point`]) : undefined}
                isController={isController}
                teamColor={team1Color}
                key={`team1-serving-${isTeam1Serving}-${isTiebreakActiveForDisplay ? displayTiebreakPoints.team1 : matchData?.scores?.team1 || 0}`}
                team1Name={team1Name}
                team2Name={team2Name}
                isTeam1={true}
                lastScoringTeam={tennisLastScoringTeam}
              />
              <TennisScoreBox
                score={isTiebreakActiveForDisplay ? displayTiebreakPoints.team2.toString() : getDisplayScores(matchData?.scores?.team1 || 0, matchData?.scores?.team2 || 0).score2}
                name={team2Name}
                color={getHexFromColor(team2Color)}
                dark={tennisIsDarkTheme}
                onClick={isController ? () => handleTennisCommand([`${team2Color} point`]) : undefined}
                isController={isController}
                teamColor={team2Color}
                key={`team2-serving-${isTeam1Serving}-${isTiebreakActiveForDisplay ? displayTiebreakPoints.team2 : matchData?.scores?.team2 || 0}`}
                team1Name={team1Name}
                team2Name={team2Name}
                isTeam1={false}
                lastScoringTeam={tennisLastScoringTeam}
              />
            </>
          ) : (
            <>
              <TennisScoreBox
                score={isTiebreakActiveForDisplay ? displayTiebreakPoints.team2.toString() : getDisplayScores(matchData?.scores?.team1 || 0, matchData?.scores?.team2 || 0).score2}
                name={team2Name}
                color={getHexFromColor(team2Color)}
                dark={tennisIsDarkTheme}
                onClick={isController ? () => handleTennisCommand([`${team2Color} point`]) : undefined}
                isController={isController}
                teamColor={team2Color}
                key={`team2-serving-${isTeam1Serving}-${isTiebreakActiveForDisplay ? displayTiebreakPoints.team2 : matchData?.scores?.team2 || 0}`}
                team1Name={team1Name}
                team2Name={team2Name}
                isTeam1={false}
                lastScoringTeam={tennisLastScoringTeam}
              />
              <TennisScoreBox
                score={isTiebreakActiveForDisplay ? displayTiebreakPoints.team1.toString() : getDisplayScores(matchData?.scores?.team1 || 0, matchData?.scores?.team2 || 0).score1}
                name={team1Name}
                color={getHexFromColor(team1Color)}
                dark={tennisIsDarkTheme}
                onClick={isController ? () => handleTennisCommand([`${team1Color} point`]) : undefined}
                isController={isController}
                teamColor={team1Color}
                key={`team1-serving-${isTeam1Serving}-${isTiebreakActiveForDisplay ? displayTiebreakPoints.team1 : matchData?.scores?.team1 || 0}`}
                team1Name={team1Name}
                team2Name={team2Name}
                isTeam1={true}
                lastScoringTeam={tennisLastScoringTeam}
              />
            </>
          )}
        </Flex>

        <VStack spacing={2} align="center">
          <HStack spacing={2} justify="center">
            <Button
              mt={0}
              colorScheme="blue"
              variant="outline"
              size="sm"
              onClick={() => handleTennisCommand(['reverse point'])}
              isDisabled={tennisIsInCooldown.current || !!tennisPendingGameCompletion || !!tennisPendingTiebreakCompletion || !!tennisPendingSetCompletion}
              opacity={tennisIsInCooldown.current || !!tennisPendingGameCompletion || !!tennisPendingTiebreakCompletion || !!tennisPendingSetCompletion ? 0.5 : 1}
              _hover={{ bg: 'blue.500', color: 'white' }}
              color="#747474"
              borderRadius="2px"
            >
              Say "Reverse Point"
            </Button>
            <Button
              mt={0}
              colorScheme="blue"
              variant="outline"
              size="sm"
              onClick={() => handleTennisCommand(['reset match'])}
              isDisabled={tennisIsInCooldown.current || !!tennisPendingGameCompletion || !!tennisPendingTiebreakCompletion || !!tennisPendingSetCompletion}
              opacity={tennisIsInCooldown.current || !!tennisPendingGameCompletion || !!tennisPendingTiebreakCompletion || !!tennisPendingSetCompletion ? 0.5 : 1}
              _hover={{ bg: 'blue.500', color: 'white' }}
              color="#747474"
              borderRadius="2px"
            >
              Say "Reset Match"
            </Button>
          </HStack>
          <ClubLogo variant="inline" responsive={true} />
        </VStack>
      </VStack>

      {/* Progress bar */}
      <Box position="fixed" bottom={0} left={0} right={0} width="100vw" margin={0} padding={0}>
        {isController && isQRCodeVisible && (
          <Box
            position="fixed"
            bottom={4}
            left={4}
            bg="white"
            p={2}
            borderRadius="md"
            boxShadow="lg"
            zIndex={1001}
          >
            <QRCode
              value={`${window.location.origin}?matchId=${matchId}&token=${tennisControllerToken}&controller=true`}
              size={100}
              style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
            />
          </Box>
        )}
        <Box position="fixed" bottom={0} left={0} right={0} width="100vw" margin={0} padding={0}>
          <Text
            position="absolute"
            left="50%"
            bottom="4px"
            transform="translateX(-50%)"
            color="white"
            fontSize="sm"
            fontWeight="normal"
            zIndex={1001}
          >
            AiScoringApp.com
          </Text>
        </Box>
      </Box>

      {/* Theme toggle */}
      <Box position="fixed" bottom={4} right={4} zIndex={1002}>
        <HStack spacing={3} align="center">
          <FormControl display="flex" alignItems="center">
            <FormLabel mb="0" color="#747474" fontWeight="normal"></FormLabel>
            <Switch
              isChecked={tennisIsDarkTheme}
              onChange={(e) => setTennisIsDarkTheme(e.target.checked)}
              colorScheme="blue"
            />
          </FormControl>
          {courtNumber && !isIndividualPlan && (
            <Box
              color="white"
              px={3}
              py={1}
              fontSize="lg"
              fontWeight="bold"
              whiteSpace="nowrap"
            >
              Court {courtNumber}
            </Box>
          )}
        </HStack>
      </Box>



      {/* Microphone Settings Modal */}
      <MicrophoneSettingsModal
        isOpen={tennisShowMicrophoneSettings}
        onClose={() => setTennisShowMicrophoneSettings(false)}
      />

      {/* Statistics Modal */}
      <Statistics
        isOpen={tennisShowStatistics}
        onClose={() => setTennisShowStatistics(false)}
        matchId={matchId}
        team1Color={team1Color}
        team2Color={team2Color}
        team1Name={team1Name}
        team2Name={team2Name}
        setScores={tennisSetScores}
        currentSetScore={{
          team1: (matchData?.gameScores || []).filter((score: { team1: number; team2: number }) => score.team1 > score.team2).length,
          team2: (matchData?.gameScores || []).filter((score: { team1: number; team2: number }) => score.team2 > score.team1).length
        }}
      />

      {/* Game Completion Popup */}
      <ScaleFade in={tennisPendingGameCompletion !== null} initialScale={0.9} unmountOnExit>
        <Box
          position="fixed"
          top="20%"
          left="50%"
          transform="translateX(-50%)"
          bg={tennisPendingGameCompletion?.team === 'team1' ? 
            (team1Color === 'blue' ? '#2e4fcb' : 
             team1Color === 'red' ? '#dd0000' : 
             team1Color === 'green' ? '#38a169' : 
             team1Color === 'orange' ? '#f73e01' : 
             team1Color === 'purple' ? '#8f00c7' : 
             team1Color === 'yellow' ? '#e2e700' : `${team1Color}.100`) :
            (team2Color === 'blue' ? '#2e4fcb' : 
             team2Color === 'red' ? '#dd0000' : 
             team2Color === 'green' ? '#38a169' : 
             team2Color === 'orange' ? '#f73e01' : 
             team2Color === 'purple' ? '#8f00c7' : 
             team2Color === 'yellow' ? '#e2e700' : `${team2Color}.100`)}
          color={tennisPendingGameCompletion?.team === 'team1' ? 
            (team1Color === 'yellow' ? 'black' : 'white') :
            (team2Color === 'yellow' ? 'black' : 'white')}
          p={6}
          borderRadius="lg"
          textAlign="center"
          zIndex={1000}
          boxShadow="0 8px 32px rgba(0, 0, 0, 0.8)"
          minWidth="800px"
          maxWidth="1400px"
        >
          <Text fontSize="120px" fontWeight="bold" mb={2}>
            GAME!
          </Text>
          <Text fontSize="2xl">
            {tennisGameCompletionTimer}
          </Text>
          <Text fontSize="lg" mt={2} mb={4}>
            {isController ? (
              <></>
            ) : (
              <>Display Only</>
            )}
          </Text>
          {isController && (
            <Button
              colorScheme="red"
              variant="outline"
              size="lg"
              onClick={() => handleTennisCommand(['cancel'])}
              bg="#dd0000"
              color="white"
              border="2px solid"
              borderColor="#dd0000"
              mt={2}
            >
              Say "Cancel" to stop
            </Button>
          )}
        </Box>
      </ScaleFade>

      {/* Tie-Break Completion Popup */}
      <ScaleFade in={tennisPendingTiebreakCompletion !== null} initialScale={0.9} unmountOnExit>
        <Box
          position="fixed"
          top="20%"
          left="50%"
          transform="translateX(-50%)"
          bg={tennisPendingTiebreakCompletion?.team === 'team1' ? 
            (team1Color === 'blue' ? '#2e4fcb' : 
             team1Color === 'red' ? '#dd0000' : 
             team1Color === 'green' ? '#38a169' : 
             team1Color === 'orange' ? '#f73e01' : 
             team1Color === 'purple' ? '#8f00c7' : 
             team1Color === 'yellow' ? '#e2e700' : `${team1Color}.100`) :
            (team2Color === 'blue' ? '#2e4fcb' : 
             team2Color === 'red' ? '#dd0000' : 
             team2Color === 'green' ? '#38a169' : 
             team2Color === 'orange' ? '#f73e01' : 
             team2Color === 'purple' ? '#8f00c7' : 
             team2Color === 'yellow' ? '#e2e700' : `${team2Color}.100`)}
          color={tennisPendingTiebreakCompletion?.team === 'team1' ? 
            (team1Color === 'yellow' ? 'black' : 'white') :
            (team2Color === 'yellow' ? 'black' : 'white')}
          p={6}
          borderRadius="lg"
          textAlign="center"
          zIndex={1000}
          boxShadow="0 8px 32px rgba(0, 0, 0, 0.8)"
          minWidth="800px"
          maxWidth="1400px"
        >
          <Text fontSize="120px" fontWeight="bold" mb={2}>
            TIE-BREAK!
          </Text>
          <Text fontSize="2xl">
            {tennisTiebreakCompletionTimer}
          </Text>
          <Text fontSize="lg" mt={2} mb={4}>
            {isController ? (
              <></>
            ) : (
              <>Display Only</>
            )}
          </Text>
          {isController && (
            <Button
              colorScheme="red"
              variant="outline"
              size="lg"
              onClick={() => handleTennisCommand(['cancel'])}
              bg="#dd0000"
              color="white"
              border="2px solid"
              borderColor="#dd0000"
              mt={2}
            >
              Say "Cancel" to stop
            </Button>
          )}
        </Box>
      </ScaleFade>

      {/* Set Completion Popup - Hidden but functionality preserved */}

      {/* Overlay Components */}
      <GameResetOverlay visible={!!tennisPendingReset} />

      {showTiebreakNotification && (
        <Box
          position="fixed"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          bg="rgba(0,0,0,0.92)"
          p={12}
          borderRadius="2xl"
          zIndex={2000}
          textAlign="center"
          boxShadow="2xl"
          border="4px solid white"
        >
          <Text color="white" fontSize="5xl" fontWeight="extrabold" letterSpacing="wide">
            {isSuperTiebreak ? 'Super Tie-Break' : 'Tie-Break'}
          </Text>
        </Box>
      )}

    </Box>
  )
}
